import React, { useState, useEffect } from 'react'
import { PageHeader, Button, Table, Skeleton, Popconfirm, Modal } from 'antd'
import { useQuery } from '@apollo/react-hooks'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'

import ResultError from '@packages/ResultError'
import { ACTIVE_SUBSTANCE_FAMILIES_ICON } from '@constants/IconsConstants'
import { useColumnFilterProps } from '@utils/columnFilter'
import { getActiveSubstanceFamiliesQuery } from '@graphql/queries'
import { useUpdateActiveSubstanceFamily, useDeleteActiveSubstanceFamily } from '@graphql/mutations'

import EditableCell from '../../EditableCell'
import ManageActiveSubstanceFamilyForm from '../ManageActiveSubstanceFamilyForm'

const tableProps = {
  size: 'middle',
  pagination: false,
}
const newButtonProps = {
  type: 'primary',
  icon: <PlusOutlined />,
  key: 'newActiveSubstanceFamily',
}

function ManageActiveSubstanceFamilyTable() {
  const [activeSubstanceFamiliesData, setActiveSubstanceFamiliesData] = useState(false)
  const [formModalVisible, setFormModalVisible] = useState(false)
  const { error, data } = useQuery(getActiveSubstanceFamiliesQuery)
  const [updateActiveSubstanceFamily] = useUpdateActiveSubstanceFamily()
  const [deleteActiveSubstanceFamily] = useDeleteActiveSubstanceFamily()
  const useFilterField = field => useColumnFilterProps(field)
  const toggleFormModal = () => setFormModalVisible(!formModalVisible)

  useEffect(() => {
    if (data) setActiveSubstanceFamiliesData(data.activeSubstanceFamilies)
  }, [data])

  const handleSave = data => {
    const { id, name } = data
    const newData = [...activeSubstanceFamiliesData]
    const index = newData.findIndex(item => item.id === data.id)
    const item = newData[index]

    newData.splice(index, 1, { ...item, ...data })

    if (JSON.stringify(newData) !== JSON.stringify(activeSubstanceFamiliesData)) {
      updateActiveSubstanceFamily({
        variables: {
          id,
          data: {
            name: { set: name },
          },
        },
      })

      setActiveSubstanceFamiliesData(newData)
    }
  }

  const handleDelete = id => {
    deleteActiveSubstanceFamily({
      variables: { id },
      refetchQueries: ['GetActiveSubstanceFamiliesQuery'],
    })
  }
  const columns = [
    {
      dataIndex: 'name',
      title: 'Nome',
      editable: true,
      ...useFilterField('name'),
    },
    {
      align: 'center',
      render: (value, { id, name }) => (
        <Popconfirm
          title={`Tem a certeza que quer eliminar ${name} ?`}
          onConfirm={() => handleDelete(id)}
          okText="Sim"
          cancelText="Não"
        >
          <DeleteOutlined />
        </Popconfirm>
      ),
    },
  ]
  const mappedColumns = columns.map(col => {
    if (!col.editable) return col

    return {
      ...col,
      onCell: record => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    }
  })

  return (
    <>
      <Modal
        title="Novo tipo de aplicação"
        visible={formModalVisible}
        onCancel={() => toggleFormModal()}
        footer={null}
      >
        <ManageActiveSubstanceFamilyForm afterSubmit={() => toggleFormModal()} />
      </Modal>

      <PageHeader
        title="Familias Substâncias Activas"
        avatar={{ src: ACTIVE_SUBSTANCE_FAMILIES_ICON }}
        extra={[
          <Button {...newButtonProps} onClick={() => toggleFormModal()}>
            Nova familía
          </Button>,
        ]}
      />

      {error && <ResultError />}

      <Skeleton loading={!error && !activeSubstanceFamiliesData} active>
        {activeSubstanceFamiliesData && (
          <Table
            components={EditableCell.Components}
            columns={mappedColumns}
            dataSource={activeSubstanceFamiliesData}
            rowKey={({ id }) => id}
            rowClassName={() => 'editable-row'}
            {...tableProps}
          />
        )}
      </Skeleton>
    </>
  )
}

export default ManageActiveSubstanceFamilyTable
